import { useState, useCallback, useMemo } from "react";
import { type ZodType, type ZodError, type output as ZodOutput } from "zod";

export interface FieldErrors {
  [path: string]: string;
}

export function useFormValidation<S extends ZodType>(schema: S, data: unknown) {
  type T = ZodOutput<S>;
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const result = useMemo(() => schema.safeParse(data), [schema, data]);

  const allErrors: FieldErrors = useMemo(() => {
    if (result.success) return {};
    const errs: FieldErrors = {};
    for (const issue of (result as { success: false; error: ZodError }).error.issues) {
      const path = issue.path.join(".");
      if (!errs[path]) errs[path] = issue.message;
    }
    return errs;
  }, [result]);

  /** Only return errors for fields the user has interacted with. */
  const errors: FieldErrors = useMemo(() => {
    const visible: FieldErrors = {};
    for (const [path, msg] of Object.entries(allErrors)) {
      if (touched.has(path)) visible[path] = msg;
    }
    return visible;
  }, [allErrors, touched]);

  const touch = useCallback(
    (field: string) => setTouched((prev) => new Set(prev).add(field)),
    [],
  );

  const touchAll = useCallback(() => {
    setTouched(new Set(Object.keys(allErrors)));
  }, [allErrors]);

  const isValid = result.success;
  const parsed: T | null = result.success ? (result.data as T) : null;

  return { errors, allErrors, isValid, parsed, touch, touchAll };
}
